import { recordingLeaveGuard } from '@sh/services/recording/recording-session.service';
import { inject, NgModule } from '@angular/core';
import { CanActivateFn, RouterModule, Routes } from '@angular/router';
import { ProfileFacadeService } from '@sh/facades/profile-facade.service';
import { authGuard } from '@sh/guards/auth.guard';
import { AboutComponent } from '@sh/pages/about/about.component';
import { LoginComponent } from '@sh/pages/auth/login/login.component';
import { SignupComponent } from '@sh/pages/auth/signup/signup.component';
import { ContactComponent } from '@sh/pages/contact/contact.component';
import { HomeComponent } from '@sh/pages/home/home.component';
import { NewsComponent } from '@sh/pages/news/news.component';
import { PrivacyComponent } from '@sh/pages/privacy/privacy.component';
import { ProfileComponent } from '@sh/pages/profile/profile.component';
import { TermsComponent } from '@sh/pages/terms/terms.component';
import { map } from 'rxjs';
import { NotFoundComponent } from './pages/not-found/not-found.component';
import { ChangelogComponent } from './pages/changelog/changelog.component';
import { CookieComponent } from './pages/cookie/cookie.component';
import { GetInvolvedComponent } from './pages/get-involved/get-involved.component';
import { MotherTongueEducationComponent } from './pages/mother-tongue-education/mother-tongue-education.component';

const profileLoaded: CanActivateFn = (route, state) =>
  inject(ProfileFacadeService).profile$.pipe(
    map((profile: any) => profile !== null),
  );

const routes: Routes = [
  {
    path: '',
    component: HomeComponent,
    canDeactivate: [recordingLeaveGuard],
    runGuardsAndResolvers: 'paramsOrQueryParamsChange',
    data: {
      seo: {
        title: 'Free Learning Resources and Study Help',
        description:
          'Find free books, videos, interactive activities and classroom-ready learning resources created with teachers, students and local education communities.',
        keywords:
          'free learning resources, study help, teacher resources, education app, curriculum resources, Study Heroes',
      },
    },
  },
  {
    path: 'login',
    component: LoginComponent,
    data: {
      seo: {
        title: 'Sign In',
        description:
          'Sign in to Study Heroes to manage resources, shelves, books and learning contributions.',
        noIndex: true,
      },
    },
  },
  {
    path: 'signup',
    component: SignupComponent,
    data: {
      seo: {
        title: 'Create a Free Study Heroes Account',
        description:
          'Join Study Heroes to save learning materials, contribute resources and help improve access to education.',
      },
    },
  },
  {
    path: 'profile',
    component: ProfileComponent,
    canActivate: [authGuard],
    data: {
      seo: {
        title: 'Your Profile',
        description: 'Manage your Study Heroes profile and education activity.',
        noIndex: true,
      },
    },
  },
  {
    path: 'about',
    component: AboutComponent,
    data: {
      seo: {
        title: 'About Study Heroes',
        description:
          'Learn how Study Heroes is building a community-powered education platform for students, teachers and content creators.',
        schema: {
          '@type': 'AboutPage',
          name: 'About Study Heroes',
          description:
            'Study Heroes is a community-powered education platform for students, teachers and content creators.',
        },
      },
    },
  },
  {
    path: 'contact',
    component: ContactComponent,
    data: {
      seo: {
        title: 'Contact Study Heroes',
        description:
          'Contact Study Heroes about learning resources, partnerships, content creation, support or education community projects.',
      },
    },
  },
  {
    path: 'news',
    component: NewsComponent,
    data: {
      seo: {
        title: 'Education News and Updates',
        description:
          'Read Study Heroes news, education updates and progress on community learning tools.',
      },
    },
  },
  {
    path: 'changelog',
    component: ChangelogComponent,
    data: {
      seo: {
        title: 'Product Updates',
        description:
          'See the latest Study Heroes product improvements, platform updates and learning resource changes.',
      },
    },
  },
  {
    path: 'privacy',
    component: PrivacyComponent,
    data: {
      seo: {
        title: 'Privacy Policy',
        description:
          'Read the Study Heroes privacy policy for information about data collection, use and protection.',
      },
    },
  },
  {
    path: 'terms',
    component: TermsComponent,
    data: {
      seo: {
        title: 'Terms of Use',
        description:
          'Read the Study Heroes terms of use for learners, teachers, contributors and visitors.',
      },
    },
  },
  {
    path: 'cookie',
    component: CookieComponent,
    data: {
      seo: {
        title: 'Cookie Policy',
        description:
          'Learn how Study Heroes uses cookies and similar technologies on the education platform.',
      },
    },
  },
  {
    path: 'books',
    canActivate: [profileLoaded],
    data: {
      seo: {
        title: 'Books and Curriculum Textbooks',
        description:
          'Browse and manage books connected to Study Heroes learning resources, activities and classroom support.',
      },
    },
    loadChildren: () =>
      import('./modules/books/books.module').then((m) => m.BooksModule),
  },
  {
    path: 'users',
    canActivate: [profileLoaded],
    loadChildren: () =>
      import('./modules/users/users.module').then((m) => m.UsersModule),
  },
  {
    path: 'flags',
    canActivate: [profileLoaded],
    loadChildren: () =>
      import('./modules/flags/flags.module').then((m) => m.FlagsModule),
  },
  {
    path: 'formats',
    canActivate: [profileLoaded],
    loadChildren: () =>
      import('./modules/formats/formats.module').then((m) => m.FormatsModule),
  },
  {
    path: 'resources',
    data: {
      seo: {
        title: 'My Resources',
        description:
          'Search and manage the learning resources you have created on Study Heroes.',
      },
    },
    loadChildren: () =>
      import('./modules/resources/resources.module').then(
        (m) => m.ResourcesModule,
      ),
  },
  {
    path: 'shelves',
    data: {
      seo: {
        title: 'Learning Shelves',
        description:
          'Create, organize and share shelves of favourite books, study resources and teaching materials on Study Heroes.',
        keywords:
          'learning shelves, resource collections, study collections, teacher resource library, education bookmarks',
      },
    },
    loadChildren: () =>
      import('./modules/shelves/shelves.module').then((m) => m.ShelvesModule),
  },
  {
    path: 'categories',
    canActivate: [profileLoaded],
    loadChildren: () => import('./modules/categories/routes'),
  },
  // {
  //   path: 'donate',
  //   component: DonationComponent,
  // },
  {
    path: 'get-involved',
    component: GetInvolvedComponent,
    data: {
      seo: {
        title: 'Get Involved in Education',
        description:
          'Help Study Heroes create, translate, review and share learning resources for students, teachers and communities.',
        schema: {
          '@type': 'WebPage',
          name: 'Get Involved in Education',
          description:
            'Ways to support Study Heroes by sharing, creating, translating and reviewing learning resources.',
        },
      },
    },
  },
  {
    path: 'mother-tongue-education-benefits',
    component: MotherTongueEducationComponent,
    data: {
      seo: {
        title: 'Mother Tongue Education Benefits',
        description:
          'Learn why mother tongue education improves comprehension, confidence and student outcomes, with research-backed benefits.',
        keywords:
          'mother tongue education, multilingual learning, home language learning, education research, student success',
        type: 'article',
        schema: {
          '@type': 'Article',
          headline: 'Mother Tongue Education Benefits',
          description:
            'Research-backed benefits of mother tongue education for comprehension, confidence and student outcomes.',
          image:
            'https://studyheroes.app/assets/images/og-study-heroes-brand-v3.webp',
          author: {
            '@type': 'Organization',
            name: 'Study Heroes',
          },
          about: [
            'Mother tongue education',
            'Multilingual learning',
            'Home language learning',
          ],
        },
      },
    },
  },
  {
    path: 'create-resource',
    redirectTo: 'resources/create',
    pathMatch: 'full',
  },

  {
    path: 'documentation',
    loadChildren: () =>
      import('./pages/documentation/documentation.module').then(
        (m) => m.DocumentationModule,
      ),
  },
  {
    path: 'search',
    loadChildren: () =>
      import('./modules/search-results/search-results.module').then(
        (m) => m.SearchResultsModule,
      ),
  },
  {
    path: '**',
    component: NotFoundComponent,
    data: { seo: { title: 'Page Not Found', noIndex: true } },
  },
];

@NgModule({
  imports: [RouterModule.forRoot(routes)],
  exports: [RouterModule],
})
export class AppRoutingModule {}
